import { z } from "zod";

import { richTextSchema } from "./rich-text";

const baseFormFieldsSchema = z.object({
  label: z.string(),
  hideLabel: z.boolean().nullish(),
  name: z.string(),
  required: z.boolean(),
  placeholder: z.string().nullish(),
});

export const formFieldsSchema = [
  baseFormFieldsSchema.merge(
    z.object({
      _type: z.literal("textField"),
      type: z.enum(["text", "email", "number", "url", "tel"]),
    })
  ),
  baseFormFieldsSchema.omit({ placeholder: true }).merge(
    z.object({
      _type: z.literal("checkbox"),
      defaultValue: z.boolean().nullish(),
    })
  ),
  baseFormFieldsSchema.merge(
    z.object({
      _type: z.literal("dropdown"),
      options: z.array(
        z.object({
          label: z.string(),
          value: z.string().nullish(),
          /** Sent instead of `value` on submit (see components/forms/submit-values.ts). */
          submitValue: z.string().nullish(),
        })
      ),
      /**
       * Optional headings that split `options` in the dropdown (e.g. "Consumer
       * brands" / "Other businesses"). `options` must still list every option
       * so submission and validation are unchanged.
       */
      optionGroups: z
        .array(
          z.object({
            label: z.string(),
            options: z.array(
              z.object({
                label: z.string(),
                value: z.string().nullish(),
                /** Sent instead of `value` on submit (see components/forms/submit-values.ts). */
                submitValue: z.string().nullish(),
              })
            ),
          })
        )
        .nullish(),
    })
  ),
  /**
   * Not an input. An informational note, placed in the field list, that shows
   * when `watch` holds a value outside `eligibleValues`, and can retag the
   * lead on submit. See components/forms/eligibility-gate.ts.
   */
  z.object({
    _type: z.literal("eligibilityNote"),
    name: z.string(),
    /** Name of the field to watch. */
    watch: z.string(),
    eligibleValues: z.array(z.string()),
    message: z.string(),
    /** Replaces the submit button label while the value is ineligible. */
    ineligibleSubmitLabel: z.string().nullish(),
    /**
     * Where to send visitors with an ineligible value, instead of submitting
     * the form. UTMs are added to the URL; see eligibility-gate.ts.
     */
    ineligibleRedirect: z
      .object({
        url: z.string(),
        utm: z.object({
          source: z.string(),
          medium: z.string(),
          campaign: z.string(),
          content: z.string(),
        }),
      })
      .nullish(),
  }),
] as const;

export const formSchema = z.object({
  _type: z.literal("form"),
  fields: z.array(
    z.discriminatedUnion("_type", [
      ...formFieldsSchema,
      z.object({
        _type: z.literal("fieldset"),
        legend: z.string(),
        hideLegend: z.boolean().nullish(),
        fields: z.array(z.discriminatedUnion("_type", [...formFieldsSchema])),
        layout: z.enum(["grid", "stack"]),
      }),
    ])
  ),
  name: z.string(),
  id: z.string(),
  guid: z.string(),
  adrollSegmentId: z.string().nullish(),
  disclaimerText: richTextSchema.nullish(),
  submitButtonText: z.string(),
  successState: z.object({
    // TODO - move title into description
    title: z.string().optional(),
    description: richTextSchema,
    /** Optional next step offered once the form has been submitted. */
    cta: z
      .object({
        label: z.string(),
        href: z.string(),
        /** Routes through ExternalLink, which app.wayflyer.com links need. */
        external: z.boolean().optional(),
      })
      .optional(),
  }),
  background: z.enum(["light", "gray", "dark"]).optional(),
});
